type LessonBudgetSource = {
  attributes?: {
    estimatePayment?: unknown;
    estimateRefund?: unknown;
  };
  estimatePayment?: unknown;
  estimateRefund?: unknown;
};

type CourseTeacherBudgetBalance = {
  teacherPayments?: {
    estimatedOutflow?: unknown;
  };
  teacherRefunds?: {
    estimatedOutflow?: unknown;
  };
};

export type CourseTeacherBudgetMismatch = {
  field: 'teacherPayments' | 'teacherRefunds';
  budgetLabel: string;
  lessonsLabel: string;
  budgetAmount: number;
  lessonsAmount: number;
  message: string;
};

export const normalizeBudgetAmount = (value: unknown): number => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return Math.round((parsedValue + Number.EPSILON) * 100) / 100;
};

export const formatBudgetAmount = (value: unknown): string =>
  `€ ${normalizeBudgetAmount(value).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const getCourseLessonsTeacherBudgetTotals = (
  lessons: LessonBudgetSource[] = [],
) =>
  lessons.reduce(
    (totals, lesson) => {
      const lessonBudget = lesson?.attributes || lesson || {};

      return {
        estimatedTeacherPayment: normalizeBudgetAmount(
          totals.estimatedTeacherPayment +
            normalizeBudgetAmount(lessonBudget.estimatePayment),
        ),
        estimatedTeacherRefund: normalizeBudgetAmount(
          totals.estimatedTeacherRefund +
            normalizeBudgetAmount(lessonBudget.estimateRefund),
        ),
      };
    },
    {
      estimatedTeacherPayment: 0,
      estimatedTeacherRefund: 0,
    },
  );

const buildMismatchMessage = ({
  budgetLabel,
  lessonsLabel,
  budgetAmount,
  lessonsAmount,
}: Omit<CourseTeacherBudgetMismatch, 'field' | 'message'>) =>
  `Impossibile salvare: il totale ${budgetLabel} a preventivo (${formatBudgetAmount(
    budgetAmount,
  )}) non coincide con il totale ${lessonsLabel} (${formatBudgetAmount(
    lessonsAmount,
  )}). Aggiorna le lezioni oppure ripristina il valore del bilancio.`;

export const getCourseTeacherBudgetMismatch = ({
  balance,
  lessons,
}: {
  balance?: CourseTeacherBudgetBalance;
  lessons?: LessonBudgetSource[];
}): CourseTeacherBudgetMismatch | undefined => {
  const { estimatedTeacherPayment, estimatedTeacherRefund } =
    getCourseLessonsTeacherBudgetTotals(lessons);

  const budgetTeacherPayment = normalizeBudgetAmount(
    balance?.teacherPayments?.estimatedOutflow,
  );
  const budgetTeacherRefund = normalizeBudgetAmount(
    balance?.teacherRefunds?.estimatedOutflow,
  );

  if (budgetTeacherPayment !== estimatedTeacherPayment) {
    const mismatch = {
      field: 'teacherPayments' as const,
      budgetLabel: 'compensi docenti',
      lessonsLabel: 'compensi delle lezioni',
      budgetAmount: budgetTeacherPayment,
      lessonsAmount: estimatedTeacherPayment,
    };

    return {
      ...mismatch,
      message: buildMismatchMessage(mismatch),
    };
  }

  if (budgetTeacherRefund !== estimatedTeacherRefund) {
    const mismatch = {
      field: 'teacherRefunds' as const,
      budgetLabel: 'rimborsi docenti',
      lessonsLabel: 'rimborsi delle lezioni',
      budgetAmount: budgetTeacherRefund,
      lessonsAmount: estimatedTeacherRefund,
    };

    return {
      ...mismatch,
      message: buildMismatchMessage(mismatch),
    };
  }

  return undefined;
};
